import { useCallback, useEffect, useState } from "react";
import { Button, Chip, Switch, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from "@heroui/react";
import { api, fmtTime, type AuditRow, type GateName } from "../lib/api.ts";
import { useLab, useRun } from "../lib/state.tsx";
import { Section, Stat } from "../components/Section.tsx";

const ORDER: GateName[] = ["identity", "privilege", "catalog", "egress", "audit"];

const DETAIL: Record<GateName, string> = {
  identity:
    "Every agent presents its own token and its own scopes. A shared service account cannot answer 'which agent did this?' after the fact.",
  privilege:
    "Read-only by default. A tool that mutates state runs only if the caller holds a matching write scope — granted per tool, never per service.",
  catalog:
    "The hash of an approved tool definition is pinned. If the definition changes afterwards the call is refused until a human re-approves it.",
  egress:
    "The sandbox's fetch shim may reach this server's MCP endpoint and nothing else — not another path on the same origin, and certainly not the internet.",
  audit:
    "Every call is written to an append-only log with its decision and the gate that made it. ADR-010: never truncated by an operational job.",
};

interface AttackResult {
  succeeded: boolean;
  verdict: string;
  sandbox: { egressBlocked: number };
}

export function GatesPanel() {
  const { gates, setGate, refresh } = useLab();
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const attack = useRun<AttackResult>();

  const loadAudit = useCallback(async () => {
    setAudit((await api.audit(120)).entries);
  }, []);

  useEffect(() => {
    void loadAudit();
    const t = setInterval(() => void loadAudit(), 3000);
    return () => clearInterval(t);
  }, [loadAudit]);

  const on = ORDER.filter((g) => gates?.gates[g]).length;
  const denied = audit.filter((a) => a.decision.startsWith("denied")).length;

  return (
    <div className="flex flex-col gap-4">
      <Section
        title="Five gates, each one deterministic"
        blurb="None of these needs a model to decide anything. That is the point: the defences that hold are the boring ones a reviewer can read in an afternoon."
      >
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <Stat label="Gates on" value={`${on} / 5`} tone={on === 5 ? "success" : "warning"} />
          <Stat label="Pinned definitions" value={gates?.pins ?? 0} sub="approved tool hashes" />
          <Stat label="Denials in the log" value={denied} tone={denied ? "danger" : "default"} />
        </div>

        <div className="flex flex-col gap-2">
          {ORDER.map((gate) => (
            <div
              key={gate}
              data-testid={`gate-${gate}`}
              data-on={gates?.gates[gate] ? "1" : "0"}
              className={`flex items-start gap-3 rounded-medium border px-3 py-2 ${
                gates?.gates[gate] ? "border-success/40 bg-success/5" : "border-danger/40 bg-danger/5"
              }`}
            >
              <Switch
                size="sm"
                className="mt-0.5"
                color="success"
                isSelected={gates?.gates[gate] ?? false}
                onValueChange={(v) => void setGate(gate, v)}
                aria-label={gate}
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{gate}</span>
                  <Chip
                    size="sm"
                    variant="flat"
                    color={gates?.gates[gate] ? "success" : "danger"}
                    className="h-5 font-mono text-[10px]"
                  >
                    {gates?.gates[gate] ? "ON" : "OFF"}
                  </Chip>
                </div>
                <p className="mt-0.5 text-xs text-default-400">{DETAIL[gate]}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant="flat"
            onPress={async () => {
              await api.setGates({ reset: true });
              await refresh();
            }}
          >
            Reset to secure defaults
          </Button>
          <Button
            size="sm"
            variant="flat"
            onPress={async () => {
              await api.approveAll();
              await refresh();
            }}
          >
            Approve the whole catalogue
          </Button>
          <Button
            size="sm"
            variant="flat"
            onPress={async () => {
              await api.clearPins();
              await refresh();
            }}
          >
            Clear pins
          </Button>
          <Button
            size="sm"
            color="danger"
            variant="flat"
            isLoading={attack.pending}
            onPress={() => void attack.run("attack")}
          >
            Re-run the attack with these settings
          </Button>
          {attack.data && (
            <Chip size="sm" color={attack.data.succeeded ? "danger" : "success"} variant="flat">
              {attack.data.succeeded ? "exfiltration succeeded" : "blocked"}
            </Chip>
          )}
        </div>
      </Section>

      <Section
        title="The audit log"
        blurb="Every call in the whole app lands here — allowed or denied, with the gate that decided and the identity that asked."
      >
        <Table
          aria-label="Audit log"
          removeWrapper
          isCompact
          classNames={{ base: "max-h-[26rem] overflow-y-auto wire-scroll" }}
        >
          <TableHeader>
            <TableColumn>TIME</TableColumn>
            <TableColumn>AGENT</TableColumn>
            <TableColumn>PROTO</TableColumn>
            <TableColumn>METHOD</TableColumn>
            <TableColumn>TOOL</TableColumn>
            <TableColumn>DECISION</TableColumn>
            <TableColumn>MS</TableColumn>
          </TableHeader>
          <TableBody emptyContent="Nothing yet — run any demo.">
            {audit.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-mono text-[10px] text-default-500">
                  {fmtTime(row.ts)}
                </TableCell>
                <TableCell className="font-mono text-[11px]">{row.agent ?? "—"}</TableCell>
                <TableCell className="font-mono text-[10px] text-default-500">
                  {row.protocol}
                </TableCell>
                <TableCell className="font-mono text-[11px]">{row.method}</TableCell>
                <TableCell className="font-mono text-[11px] text-default-400">
                  {row.tool_name ?? "—"}
                </TableCell>
                <TableCell>
                  <Chip
                    size="sm"
                    variant="flat"
                    className="h-5 font-mono text-[10px]"
                    color={
                      row.decision === "allowed"
                        ? "success"
                        : row.decision.startsWith("denied")
                          ? "danger"
                          : "warning"
                    }
                  >
                    {row.decision}
                  </Chip>
                </TableCell>
                <TableCell className="font-mono text-[10px] text-default-500">
                  {row.duration_ms === null ? "—" : Math.round(row.duration_ms)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </div>
  );
}
